// ========================================
// OPTIMIZED PREDICTIVE SEARCH - FINAL VERSION
// ========================================

document.addEventListener('DOMContentLoaded', () => {
  // Run setup with a small delay to ensure all forms are in DOM
  setTimeout(() => {
    setupDesktopSearch();
    setupMobileSearch();
  }, 100);
});

// ========================================
// DESKTOP SEARCH SETUP
// ========================================
function setupDesktopSearch() {
  const searchForms = document.querySelectorAll('form[action*="/search"]');
  if (!searchForms.length) return;

  searchForms.forEach((form, formIndex) => {
    const input = form.querySelector('input[name="q"]');
    if (!input) return;

    // Skip mobile forms
    if (form.classList.contains('mobile-search-form')) return;

    // Check if THIS SPECIFIC form already has our dropdown
    const existingDropdown = form.querySelector('.predictive-search-dropdown.ps-custom');
    if (existingDropdown) return;

    // Create unique ID using form index + timestamp + random
    const uniqueId = 'search-dropdown-' + formIndex + '-' + Date.now() + '-' + Math.random().toString(36).substr(2, 9);

    const dropdown = document.createElement('div');
    dropdown.className = 'predictive-search-dropdown ps-custom';
    dropdown.setAttribute('role', 'listbox');
    dropdown.setAttribute('aria-label', 'Search suggestions');
    dropdown.id = uniqueId;
    dropdown.style.display = 'none';
    form.style.position = 'relative';
    form.appendChild(dropdown);

    // Add ARIA attributes to input with the correct dropdown ID
    input.setAttribute('aria-autocomplete', 'list');
    input.setAttribute('aria-controls', uniqueId);
    input.setAttribute('aria-expanded', 'false');
    input.setAttribute('aria-label', 'Search products');

    let debounceTimer;
    let selectedIndex = -1;
    
    input.addEventListener('input', (e) => {
      clearTimeout(debounceTimer);
      const query = input.value.trim();

      // ALWAYS hide dropdown if less than 3 characters
      if (query.length < 3) {
        dropdown.style.display = 'none';
        dropdown.innerHTML = '';
        input.setAttribute('aria-expanded', 'false');
        return;
      }

      // Wait 250ms before searching
      debounceTimer = setTimeout(() => {
        // Double-check query length before fetching
        const currentQuery = input.value.trim();
        if (currentQuery.length < 3) {
          dropdown.style.display = 'none';
          dropdown.innerHTML = '';
          input.setAttribute('aria-expanded', 'false');
          return;
        }
        
        selectedIndex = -1;
        input.setAttribute('aria-expanded', 'true');
        fetchSuggestions(currentQuery, dropdown);
      }, 250);
    }, true);

    // Close dropdown when clicking outside
    document.addEventListener('click', (e) => {
      if (!form.contains(e.target)) {
        dropdown.style.display = 'none';
        input.setAttribute('aria-expanded', 'false');
      }
    });

    // Close dropdown with Escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        dropdown.style.display = 'none';
        input.setAttribute('aria-expanded', 'false');
      }
    });

    // Keyboard navigation
    input.addEventListener('keydown', (e) => {
      const items = dropdown.querySelectorAll('a');
      if (!items.length) return;

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        selectedIndex = Math.min(selectedIndex + 1, items.length - 1);
        updateSelection(items);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        selectedIndex = Math.max(selectedIndex - 1, -1);
        updateSelection(items);
      } else if (e.key === 'Enter' && selectedIndex >= 0) {
        e.preventDefault();
        items[selectedIndex].click();
      }
    });

    function updateSelection(items) {
      items.forEach((item, index) => {
        if (index === selectedIndex) {
          item.classList.add('ps-selected');
          item.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        } else {
          item.classList.remove('ps-selected');
        }
      });
    }
  });
}

// ========================================
// MOBILE SEARCH SETUP
// ========================================
function setupMobileSearch() {
  const mobileSearchIcon = document.querySelector('.site-header__search-mobile');
  if (!mobileSearchIcon) return;

  const overlay = document.createElement('div');
  overlay.className = 'mobile-search-overlay';
  overlay.innerHTML = `
    <div class="overlay-bg"></div>
    <div class="overlay-box">
      <form action="/search" method="get" role="search" class="mobile-search-form">
        <input type="search" name="q" placeholder="Search our store..." autocomplete="off" enterkeyhint="search" aria-label="Search products">
      </form>
      <div class="mobile-predictive-search-dropdown" role="listbox" aria-label="Search suggestions"></div>
    </div>
  `;
  document.body.appendChild(overlay);

  const bg = overlay.querySelector('.overlay-bg');
  const input = overlay.querySelector('input[name="q"]');
  const dropdown = overlay.querySelector('.mobile-predictive-search-dropdown');

  mobileSearchIcon.addEventListener('click', (e) => {
    e.preventDefault();
    e.stopPropagation();
    overlay.classList.add('show');
    setTimeout(() => input.focus(), 100);
  });

  const closeOverlay = () => {
    overlay.classList.remove('show');
    dropdown.style.display = 'none';
  };

  bg.addEventListener('click', closeOverlay);
  
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') closeOverlay();
  });

  let debounceTimer;
  
  input.addEventListener('input', () => {
    clearTimeout(debounceTimer);
    const query = input.value.trim();
    
    if (query.length < 3) {
      dropdown.style.display = 'none';
      return;
    }

    debounceTimer = setTimeout(() => {
      fetchMobileSuggestions(query, dropdown);
    }, 250);
  });
}

// ========================================
// FETCH SUGGESTIONS (DESKTOP)
// ========================================
function fetchSuggestions(query, dropdown) {
  const url = `/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product,page,collection,query&resources[limit]=10&resources[limit_scope]=each`;

  dropdown.innerHTML = '<div class="ps-loading">Searching...</div>';
  dropdown.style.display = 'block';

  fetch(url)
    .then(res => res.json())
    .then(data => {
      const suggestions = data.resources?.results || {};
      let leftHTML = '';
      let rightHTML = '';

      const queries = (suggestions.queries || []).slice(0, 5);
      if (queries.length) {
        leftHTML += `<div class="ps-section-header">Popular Suggestions</div>`;
        queries.forEach(q => {
          leftHTML += `<a href="${fixMarketUrl(q.url)}" class="ps-item" role="option">${highlightText(q.text, query)}</a>`;
        });
        leftHTML += `<hr>`;
      }

      const collections = (suggestions.collections || []).slice(0, 4);
      if (collections.length) {
        leftHTML += `<div class="ps-section-header">Collections</div>`;
        collections.forEach(c => {
          leftHTML += `<a href="${fixMarketUrl(c.url)}" class="ps-item" role="option">${highlightText(c.title, query)}</a>`;
        });
        leftHTML += `<hr>`;
      }

      const pages = (suggestions.pages || []).slice(0, 3);
      if (pages.length) {
        leftHTML += `<div class="ps-section-header">Pages</div>`;
        pages.forEach(pg => {
          leftHTML += `<a href="${fixMarketUrl(pg.url)}" class="ps-item" role="option">${highlightText(pg.title, query)}</a>`;
        });
      }

      const products = (suggestions.products || []).slice(0, 10);
      if (products.length) {
        rightHTML += `<div class="ps-section-header">Products</div>`;
        products.forEach(p => {
          rightHTML += `
            <a href="${fixMarketUrl(p.url)}" class="ps-item ps-product-item" role="option">
              ${p.image ? `<img src="${p.image}" class="ps-img" alt="${escapeHTML(p.title)}">` : ''}
              <div class="ps-info">
                <span class="ps-title">${highlightText(p.title, query)}</span>
                ${p.price ? `<span class="ps-price">${formatPrice(p.price)}</span>` : ''}
              </div>
            </a>
          `;
        });
        rightHTML += `<a class="ps-see-all-btn" href="${fixMarketUrl('/search?q=' + encodeURIComponent(query) + '&type=product')}">View all matching products</a>`;
      }

      if (!leftHTML && !rightHTML) {
        dropdown.innerHTML = '<div class="ps-empty">No results found for "' + escapeHTML(query) + '"</div>';
      } else {
        const showLeftColumn = leftHTML.trim().length > 0;
        
        dropdown.innerHTML = `
          <div class="ps-columns">
            ${showLeftColumn ? `<div class="ps-column-left">${leftHTML}</div>` : ''}
            <div class="ps-column-right">${rightHTML || '<div class="ps-empty">No products found</div>'}</div>
          </div>
        `;
      }
      
      dropdown.style.display = 'block';
    })
    .catch(error => {
      console.error('Search error:', error);
      dropdown.innerHTML = '<div class="ps-error">Unable to search. Please try again.</div>';
      dropdown.style.display = 'block';
    });
}

// ========================================
// FETCH SUGGESTIONS (MOBILE)
// ========================================
function fetchMobileSuggestions(query, dropdown) {
  const url = `/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product,query&resources[limit]=10&resources[limit_scope]=each`;

  dropdown.innerHTML = '<div class="ps-loading">Searching...</div>';
  dropdown.style.display = 'block';

  fetch(url)
    .then(res => res.json())
    .then(data => {
      const suggestions = data.resources?.results || {};
      let html = '';

      const queries = (suggestions.queries || []).slice(0, 5);
      if (queries.length) {
        html += '<div class="ps-section-header">Suggestions</div>';
        queries.forEach(q => {
          html += `<a href="${fixMarketUrl(q.url)}" class="ps-item" role="option">${highlightText(q.text, query)}</a>`;
        });
      }

      const products = (suggestions.products || []).slice(0, 10);
      if (products.length) {
        html += '<div class="ps-section-header">Products</div>';
        products.forEach(p => {
          html += `
            <a href="${fixMarketUrl(p.url)}" class="ps-item ps-mobile-product-item">
              ${p.image ? `<img src="${p.image}" class="ps-mobile-img" alt="${escapeHTML(p.title)}">` : ''}
              <div class="ps-mobile-info">
                <span class="ps-mobile-title">${highlightText(p.title, query)}</span>
                ${p.price ? `<span class="ps-mobile-price">${formatPrice(p.price)}</span>` : ''}
              </div>
            </a>
          `;
        });
        
        const viewAllUrl = fixMarketUrl('/search?q=' + encodeURIComponent(query) + '&type=product');
        html += `<a class="ps-mobile-see-all-btn" href="${viewAllUrl}">View all matching products</a>`;
      }

      dropdown.innerHTML = html || '<div class="ps-item">No results found</div>';
      dropdown.style.display = 'block';
    })
    .catch(error => {
      console.error('Mobile search error:', error);
      dropdown.style.display = 'none';
    });
}

// ========================================
// HELPER: Fix URL to preserve market/locale
// ========================================
function fixMarketUrl(url) {
  if (!url) return url;
  
  const currentOrigin = window.location.origin;
  const currentPathPrefix = window.location.pathname.match(/^\/[a-z]{2}-[a-z]{2}/i);
  
  const urlObj = new URL(url, currentOrigin);
  
  if (currentPathPrefix && !urlObj.pathname.startsWith(currentPathPrefix[0])) {
    urlObj.pathname = currentPathPrefix[0] + urlObj.pathname;
  }
  
  urlObj.host = window.location.host;
  
  return urlObj.toString();
}

// ========================================
// HELPER: Escape HTML
// ========================================
function escapeHTML(text) {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

// ========================================
// HELPER: Highlight search terms
// ========================================
function highlightText(text, query) {
  if (!query) return escapeHTML(text);
  
  const escapedText = escapeHTML(text);
  const escapedQuery = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const regex = new RegExp(`(${escapedQuery})`, 'gi');
  
  return escapedText.replace(regex, '<strong>$1</strong>');
}

// ========================================
// HELPER: Format price with auto currency
// ========================================
function formatPrice(price) {
  if (!price) return '';
  
  const currency = window.Shopify?.currency?.active || 'JPY';
  
  try {
    const formatter = new Intl.NumberFormat(navigator.language, {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    });
    
    return formatter.format(price);
  } catch (error) {
    return `${price.toLocaleString()}`;
  }
}